import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Upload, Eye, Loader2 } from "lucide-react";
import AdminShell, { Panel, Btn } from "@/components/admin/AdminShell";
import AdminPager from "@/components/admin/AdminPager";
import BlogRichEditor from "@/components/admin/BlogRichEditor";
import { api, getErrorMessage } from "@/lib/api";
import { blogMediaUrl } from "@/lib/blogMedia";
import { blogBodyHasContent, normalizeBlogForm } from "@/lib/blogBody";

const emptyForm = {
  title: "",
  slug: "",
  excerpt: "",
  body: "",
  thumbnail_url: "",
  status: "draft",
  author_name: "",
};

const PAGE_SIZE = 10;

function TableSkeleton({ rows = PAGE_SIZE }) {
  return (
    <tbody data-testid="blog-table-skeleton">
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i} className="border-b border-[#1E2845]/60">
          <td className="py-3 pr-3">
            <div className="h-4 w-4 rounded bg-[#1E2845] animate-pulse" />
          </td>
          <td className="py-3 pr-3">
            <div className="w-14 h-10 rounded bg-[#1E2845] animate-pulse" />
          </td>
          <td className="py-3 pr-3">
            <div className="h-4 w-40 max-w-full rounded bg-[#1E2845] animate-pulse" />
          </td>
          <td className="py-3 pr-3">
            <div className="h-4 w-16 rounded bg-[#1E2845] animate-pulse" />
          </td>
          <td className="py-3 pr-3">
            <div className="h-4 w-24 rounded bg-[#1E2845] animate-pulse" />
          </td>
          <td className="py-3">
            <div className="h-4 w-20 rounded bg-[#1E2845] animate-pulse" />
          </td>
        </tr>
      ))}
    </tbody>
  );
}

function PostModal({ data, onClose, onSaved }) {
  const isEdit = Boolean(data?.id);
  const [f, setF] = useState(() => normalizeBlogForm(data, emptyForm));
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadInfo, setUploadInfo] = useState("");
  const thumbInputRef = useRef(null);

  function set(field, val) {
    setF((prev) => ({ ...prev, [field]: val }));
  }

  async function uploadMedia(file) {
    if (!file) throw new Error("No file selected");
    const fd = new FormData();
    fd.append("file", file);
    const { data: res } = await api.post("/api/admin/blog/upload", fd);
    if (!res?.url) throw new Error("Upload did not return a URL.");
    setUploadInfo(
      res.storage_path
        ? `Saved (${res.media_type || "file"}): ${res.storage_path}`
        : `Saved: ${res.url}`,
    );
    return { url: res.url, media_type: res.media_type || "image" };
  }

  async function uploadImage(file, onUrl) {
    if (!file) return;
    setUploading(true);
    setErr("");
    try {
      const res = await uploadMedia(file);
      if (res.media_type !== "image") {
        throw new Error("Thumbnail must be an image (JPEG, PNG, WebP, or GIF).");
      }
      onUrl(res.url);
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setUploading(false);
    }
  }

  async function uploadForEditor(file) {
    setUploading(true);
    setErr("");
    try {
      return await uploadMedia(file);
    } catch (e) {
      const msg = getErrorMessage(e);
      setErr(msg);
      throw new Error(msg);
    } finally {
      setUploading(false);
    }
  }

  async function save(e) {
    e.preventDefault();
    if (!blogBodyHasContent(f.body)) {
      setErr("Post body cannot be empty.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const payload = {
        title: f.title.trim(),
        excerpt: f.excerpt.trim(),
        body: f.body,
        thumbnail_url: f.thumbnail_url || null,
        status: f.status,
        author_name: f.author_name.trim() || undefined,
      };
      if (f.slug.trim()) payload.slug = f.slug.trim();
      if (isEdit) {
        await api.patch(`/api/admin/blog/posts/${data.id}`, payload);
      } else {
        await api.post("/api/admin/blog/posts", payload);
      }
      onSaved();
    } catch (ex) {
      setErr(getErrorMessage(ex));
    } finally {
      setBusy(false);
    }
  }

  const previewThumb = f.thumbnail_url ? blogMediaUrl(f.thumbnail_url) : "";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0F1626] border border-[#1E2845] rounded-[14px] w-full max-w-[900px] p-5 space-y-4 my-6"
        data-testid="blog-editor-modal"
      >
        <h3 className="text-[16px] font-bold">{isEdit ? "Edit post" : "New blog post"}</h3>

        <label className="block text-[12px] text-[#8892A8] font-semibold">Title</label>
        <input
          className="w-full bg-[#0A0F1F] border border-[#1E2845] rounded-lg px-3 py-2 text-[14px]"
          value={f.title ?? ""}
          onChange={(e) => set("title", e.target.value)}
          required
          data-testid="blog-field-title"
        />

        <label className="block text-[12px] text-[#8892A8] font-semibold">
          URL slug (optional — auto from title)
        </label>
        <input
          className="w-full bg-[#0A0F1F] border border-[#1E2845] rounded-lg px-3 py-2 text-[14px] font-mono"
          value={f.slug ?? ""}
          onChange={(e) => set("slug", e.target.value)}
          placeholder="my-article-title"
          data-testid="blog-field-slug"
        />

        <label className="block text-[12px] text-[#8892A8] font-semibold">Excerpt</label>
        <textarea
          className="w-full bg-[#0A0F1F] border border-[#1E2845] rounded-lg px-3 py-2 text-[14px] min-h-[72px]"
          value={f.excerpt ?? ""}
          onChange={(e) => set("excerpt", e.target.value)}
          maxLength={500}
          data-testid="blog-field-excerpt"
        />

        <div>
          <label className="block text-[12px] text-[#8892A8] font-semibold mb-2">Thumbnail</label>
          <div className="flex flex-wrap items-start gap-4">
            {previewThumb ? (
              <img
                src={previewThumb}
                alt=""
                crossOrigin="anonymous"
                referrerPolicy="no-referrer"
                className="w-40 h-24 object-cover rounded-lg border border-[#1E2845]"
              />
            ) : (
              <div className="w-40 h-24 rounded-lg border border-dashed border-[#1E2845] grid place-items-center text-[#8892A8] text-[11px]">
                No image
              </div>
            )}
            <div className="flex flex-col gap-2">
              <input
                ref={thumbInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={(e) => uploadImage(e.target.files?.[0], (url) => set("thumbnail_url", url))}
              />
              <Btn
                type="button"
                onClick={() => thumbInputRef.current?.click()}
                disabled={uploading}
                data-testid="blog-upload-thumbnail"
              >
                <Upload size={14} /> {uploading ? "Uploading…" : "Upload thumbnail"}
              </Btn>
              <input
                className="w-full min-w-[240px] bg-[#0A0F1F] border border-[#1E2845] rounded-lg px-2 py-1.5 text-[12px] font-mono"
                value={f.thumbnail_url ?? ""}
                onChange={(e) => set("thumbnail_url", e.target.value)}
                placeholder="/api/cms/blog/media/…"
              />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-[12px] text-[#8892A8] font-semibold mb-2">
            Body — use the toolbar for bold, lists, images, and videos
          </label>
          <BlogRichEditor
            value={f.body ?? ""}
            onChange={(html) => set("body", html ?? "")}
            onUploadFile={uploadForEditor}
            onUploadError={(e) => setErr(getErrorMessage(e))}
            uploading={uploading}
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[12px] text-[#8892A8] font-semibold mb-1">Status</label>
            <select
              className="w-full bg-[#0A0F1F] border border-[#1E2845] rounded-lg px-3 py-2 text-[14px]"
              value={f.status ?? "draft"}
              onChange={(e) => set("status", e.target.value)}
              data-testid="blog-field-status"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
          <div>
            <label className="block text-[12px] text-[#8892A8] font-semibold mb-1">Author name</label>
            <input
              className="w-full bg-[#0A0F1F] border border-[#1E2845] rounded-lg px-3 py-2 text-[14px]"
              value={f.author_name ?? ""}
              onChange={(e) => set("author_name", e.target.value)}
            />
          </div>
        </div>

        {f.status === "published" && f.slug ? (
          <p className="text-[12px] text-[#8892A8]">
            Public URL:{" "}
            <a
              href={`${typeof window !== "undefined" ? window.location.origin : ""}/blog/${f.slug}`}
              target="_blank"
              rel="noreferrer"
              className="text-[#F0B83A] underline"
            >
              /blog/{f.slug}
            </a>
          </p>
        ) : null}

        {uploadInfo ? (
          <p className="text-[12px] text-emerald-400/90 font-mono break-all">{uploadInfo}</p>
        ) : null}
        {err ? <p className="text-[13px] text-red-400">{err}</p> : null}

        <div className="flex justify-end gap-2 pt-2">
          <Btn type="button" onClick={onClose}>
            Cancel
          </Btn>
          <Btn type="submit" disabled={busy || uploading} data-testid="blog-save">
            {busy ? "Saving…" : "Save"}
          </Btn>
        </div>
      </form>
    </div>
  );
}

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const selectAllRef = useRef(null);

  const pageIds = useMemo(() => posts.map((p) => p.id), [posts]);
  const selectedOnPage = pageIds.filter((id) => selected.has(id));
  const allOnPageSelected = pageIds.length > 0 && selectedOnPage.length === pageIds.length;
  const someOnPageSelected = selectedOnPage.length > 0 && !allOnPageSelected;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someOnPageSelected;
    }
  }, [someOnPageSelected]);

  async function load(pageOverride) {
    const p = pageOverride ?? page;
    setLoading(true);
    try {
      const { data } = await api.get("/api/admin/blog/posts", {
        params: { limit: PAGE_SIZE, skip: (p - 1) * PAGE_SIZE },
      });
      const nextTotal = data.total || 0;
      const maxPage = Math.max(1, Math.ceil(nextTotal / PAGE_SIZE) || 1);
      if (p > maxPage) {
        setTotal(nextTotal);
        setPage(maxPage);
        return;
      }
      setPosts(data.posts || []);
      setTotal(nextTotal);
      setErr("");
    } catch (e) {
      setErr(getErrorMessage(e));
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  function toggleOne(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAllOnPage() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }

  async function remove(id) {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await api.delete(`/api/admin/blog/posts/${id}`);
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      await load();
    } catch (e) {
      alert(getErrorMessage(e));
    }
  }

  async function removeSelected() {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} selected post${ids.length === 1 ? "" : "s"} permanently?`)) return;
    setDeleting(true);
    try {
      await api.post("/api/admin/blog/posts/bulk-delete", { ids });
      setSelected(new Set());
      await load();
    } catch (e) {
      alert(getErrorMessage(e));
    } finally {
      setDeleting(false);
    }
  }

  const siteOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const showInitialSkeleton = loading && posts.length === 0 && !err;

  return (
    <AdminShell title="Blog" subtitle="Rich text posts with images, videos, and thumbnails">
      <Panel className="p-4 flex-1 min-h-0 flex flex-col">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4 shrink-0">
          <div>
            <div className="text-[14px] font-bold">Blog posts</div>
            <p className="text-[12px] text-[#8892A8] mt-1">
              Published posts appear at <code className="text-[#F0B83A]">/blog</code>. Media stored on API server.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {selected.size > 0 ? (
              <Btn
                kind="danger"
                onClick={removeSelected}
                disabled={deleting || loading}
                data-testid="blog-bulk-delete"
              >
                {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Delete selected ({selected.size})
              </Btn>
            ) : null}
            <Btn
              onClick={() => setEditing(normalizeBlogForm(null, emptyForm))}
              data-testid="blog-new"
            >
              <Plus size={14} /> New post
            </Btn>
          </div>
        </div>

        {err ? <p className="text-red-400 text-[13px] mb-3 shrink-0">{err}</p> : null}

        <div className="relative flex-1 min-h-0 overflow-auto">
          {loading && posts.length > 0 ? (
            <div
              className="absolute inset-0 z-10 bg-[#0F1626]/70 grid place-items-center"
              data-testid="blog-table-loading"
            >
              <div className="flex items-center gap-2 text-[#F0B83A] text-[13px] font-semibold">
                <Loader2 size={18} className="animate-spin" /> Loading posts…
              </div>
            </div>
          ) : null}

          <table className="w-full text-[13px]" data-testid="blog-table">
            <thead>
              <tr className="text-left text-[#8892A8] border-b border-[#1E2845]">
                <th className="pb-2 pr-3 w-10">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    className="accent-[#F0B83A] w-4 h-4 cursor-pointer"
                    checked={allOnPageSelected}
                    onChange={toggleSelectAllOnPage}
                    disabled={loading || pageIds.length === 0}
                    aria-label="Select all posts on this page"
                    data-testid="blog-select-all"
                  />
                </th>
                <th className="pb-2 pr-3">Thumb</th>
                <th className="pb-2 pr-3">Title</th>
                <th className="pb-2 pr-3">Status</th>
                <th className="pb-2 pr-3">Slug</th>
                <th className="pb-2">Actions</th>
              </tr>
            </thead>
            {showInitialSkeleton ? (
              <TableSkeleton />
            ) : (
              <tbody>
                {posts.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-10 text-center text-[#8892A8]">
                      No posts yet. Click &quot;New post&quot; to create one.
                    </td>
                  </tr>
                ) : (
                  posts.map((p) => (
                    <tr
                      key={p.id}
                      className={`border-b border-[#1E2845]/60 hover:bg-[#13192C] ${
                        selected.has(p.id) ? "bg-[#13192C]" : ""
                      }`}
                      data-testid={`blog-row-${p.id}`}
                    >
                      <td className="py-3 pr-3">
                        <input
                          type="checkbox"
                          className="accent-[#F0B83A] w-4 h-4 cursor-pointer"
                          checked={selected.has(p.id)}
                          onChange={() => toggleOne(p.id)}
                          aria-label={`Select ${p.title}`}
                          data-testid={`blog-select-${p.id}`}
                        />
                      </td>
                      <td className="py-3 pr-3">
                        {p.thumbnail_url ? (
                          <img
                            src={blogMediaUrl(p.thumbnail_url)}
                            alt=""
                            crossOrigin="anonymous"
                            referrerPolicy="no-referrer"
                            className="w-14 h-10 object-cover rounded"
                          />
                        ) : (
                          <span className="text-[#8892A8]">—</span>
                        )}
                      </td>
                      <td className="py-3 pr-3 font-semibold max-w-[200px] truncate">{p.title}</td>
                      <td className="py-3 pr-3">
                        <span
                          className={`px-2 py-0.5 rounded text-[11px] font-bold ${
                            p.status === "published"
                              ? "bg-emerald-500/15 text-emerald-400"
                              : "bg-amber-500/15 text-amber-400"
                          }`}
                        >
                          {p.status}
                        </span>
                      </td>
                      <td className="py-3 pr-3 font-mono text-[12px] text-[#8892A8]">{p.slug}</td>
                      <td className="py-3">
                        <div className="flex gap-2">
                          {p.status === "published" ? (
                            <a
                              href={`${siteOrigin}/blog/${p.slug}`}
                              target="_blank"
                              rel="noreferrer"
                              className="p-1.5 rounded hover:bg-[#1E2845] text-[#8892A8]"
                              title="View"
                            >
                              <Eye size={14} />
                            </a>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => setEditing(normalizeBlogForm(p, emptyForm))}
                            className="p-1.5 rounded hover:bg-[#1E2845] text-[#F0B83A]"
                            data-testid={`blog-edit-${p.id}`}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => remove(p.id)}
                            className="p-1.5 rounded hover:bg-[#1E2845] text-red-400"
                            data-testid={`blog-delete-${p.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            )}
          </table>
        </div>

        <AdminPager
          page={page}
          total={total}
          pageSize={PAGE_SIZE}
          loading={loading}
          onPage={setPage}
          testId="blog-pagination"
          emptyLabel="No posts"
          sticky
        />
      </Panel>

      {editing ? (
        <PostModal
          data={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            const wasNew = !editing?.id;
            setEditing(null);
            if (wasNew && page !== 1) {
              setPage(1);
            } else {
              load();
            }
          }}
        />
      ) : null}
    </AdminShell>
  );
}
